// URL <-> screen. The app was a screen-state machine with no address of its own:
// the shelf, 達悟語, and the review hub were all
// https://formosan-reader.shidailun.com/ — you could not send a teacher a link
// to their own language, and a reload always dropped you back on the shelf.
// Now the screen has an address. The screen is still the state machine; this
// module is the only translator between the two, so the mapping cannot drift.
//
// Hand-rolled rather than react-router (which was in package.json, imported
// nowhere) because there are five addresses and the interesting reader state —
// a jump's anchor word, the 片/句/段/文 level, a resumed review session — is
// deliberately NOT in the URL. Wrapping AppInner in a <Routes> tree would have
// meant re-plumbing all of that through route params to gain nothing.
//
// Reading position stays out of the URL too: it already lives per book in
// seediq_progress_v1 (see Reader.js's "view:index:anchor" save), which both
// survives a reload and rides the sync code to another device. Two sources of
// truth for "where am I" is how you get a link that silently rewinds someone.

// The slug is the book's public name, and it is NOT always the internal dialect
// key: /tao/ is what the community calls itself, while the code says `yami`
// (the exonym) because that is what sixteen data files and every vocab key are
// keyed on. It arrives on the manifest row from scripts/library_meta.mjs, so
// renaming one costs a bake, not a deploy — hence the fallback here rather than
// a table.
export const slugOf = (rowOrStory) =>
  (rowOrStory && (rowOrStory.slug || rowOrStory.dialect)) || '';

// The book within that language, and it is null for the one that lives at the
// dialect root. Sixteen shared links point at /tao/, /bunun/, /tgdaya/ — those
// had to keep meaning the Little Prince when a second book arrived, so the new
// book takes a second segment (/tgdaya/leaf/) rather than everything moving
// down one level. Comes off the manifest row; see BOOKS in scripts/library_meta.mjs.
export const bookOf = (rowOrStory) => (rowOrStory && rowOrStory.book) || null;

const SCREEN_PATHS = { srs: '/review', settings: '/settings', about: '/about', library: '/' };

// One or two segments are ours: /<lang>/ and /<lang>/<book>/. Anything deeper or
// stranger (a stray crawler path) resolves to null and the caller rewrites it to
// the shelf — the Worker's SPA fallback hands us every 404 in the origin, so an
// unknown path has to land somewhere deliberate rather than render a blank screen.
//
// A well-formed but unknown book (/tao/11) parses here and fails at rowForSlug
// instead: the two segments cannot be told apart by shape, only against the
// manifest, and the manifest is not in yet when a boot address is parsed.
const SLUG = /^[a-z][a-z0-9-]*$/;
export function parsePath(pathname) {
  const segs = String(pathname || '').replace(/^\/+|\/+$/g, '').split('/');
  const seg = segs[0];
  if (segs.length === 1) {
    if (!seg) return { screen: 'library' };
    if (seg === 'review') return { screen: 'srs' };
    if (seg === 'settings') return { screen: 'settings' };
    if (seg === 'about') return { screen: 'about' };
    if (SLUG.test(seg)) return { screen: 'reader', slug: seg, book: null };
    return null;
  }
  if (segs.length === 2 && SLUG.test(seg) && SLUG.test(segs[1])) {
    return { screen: 'reader', slug: seg, book: segs[1] };
  }
  return null;
}

// Accepts the dialect key as an alias for its slug so a hand-typed /yami/ still
// opens the book; the caller replaceState's it to the canonical /tao/, so the
// alias never becomes a second address people share.
//
// `book` null means the language's root book, and that is matched EXACTLY rather
// than by falling back to the first row in the language: a wrong /tao/leaf/ has
// to miss and land on the shelf, not quietly open the Little Prince under an
// address that says otherwise.
export function rowForSlug(lib, slug, book = null) {
  const rows = (lib && lib.stories) || [];
  const at = (key) => rows.find((s) => key(s) === slug && bookOf(s) === (book || null)) || null;
  return at(slugOf) || at((s) => s.dialect);
}

export const pathFor = (screen, row) => {
  if (screen !== 'reader' || !row) return SCREEN_PATHS[screen] || '/';
  const book = bookOf(row);
  return book ? `/${slugOf(row)}/${book}/` : `/${slugOf(row)}/`;
};
